import clsx from "clsx"
import { type HTMLAttributes, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { NavLink, useLocation, useMatch } from "react-router-dom"
import { useGlobal } from "@/app/store/global"
import { Icon } from "@/shared/components"
import type { IconName } from "@/shared/components/ui/icon"
import { Arrow } from "@/shared/components/ui/icons"
import { useMediaQuery } from "@/shared/hooks/use-media-query.ts"
import type { CustomRoute } from "@/shared/types"

const Button = (props: HTMLAttributes<HTMLDivElement> & { to: string }) => <div {...props} />

const isDev = import.meta.env.MODE === "development"

interface IProps {
  item: CustomRoute
  onClick?: VoidFunction
  depth?: number
  className?: string
  withChild?: boolean
  path?: string
  isActiveParent?: boolean
  count?: number
  tabIndex?: number
}

export const SidebarItem = ({ className, tabIndex, item, onClick, depth = 0, withChild, path, isActiveParent, count }: IProps) => {
  const { pathname } = useLocation()
  const { t } = useTranslation("sidebar")
  const match = useMatch(`dashboard/${path === "/dashboard" ? "" : path}`)
  const isActive = match != null || isActiveParent || (path?.trim() && path !== "/dashboard" ? pathname.split("/").includes(path) : false)
  const isMobile = useMediaQuery("(max-width: 1200px)")

  useEffect(() => {
    if (match != null) onClick?.()
  }, [match])

  const Component = path == null ? Button : NavLink
  if ((item.handle?.renderOnDev && !isDev) || item.handle?.hide) return null
  if (item.path == null || item.path === "") return null

  return (
    <Component
      tabIndex={tabIndex}
      to={path?.replace(/\/:.*/g, "") ?? ""}
      onKeyDown={e => {
        if (e.key === "Enter") {
          onClick?.()
          if (isMobile) useGlobal.toggleSidebar()
        }
      }}
      onClick={() => {
        onClick?.()
        if (isMobile) useGlobal.toggleSidebar()
      }}
      onMouseEnter={item?.onHover}
      className={clsx(
        "sidebar__links-link ml-1.5 border border-transparent ring-blue-400 transition focus:outline-none focus:ring-2 active:scale-95",
        className,
        isActive && "hover:!bg-[#FFFFFF2D] border-[#FFFFFF2D] bg-[#FFFFFF2D]",
        depth > 0 && "sidebar__links-inner"
      )}
    >
      <Icon
        name={(item.handle?.icon as IconName) ?? "Point"}
        className={`min-w-5 ${!item.handle?.icon && "ml-1.5"}`}
        color={isActive ? "#fff" : "#94A3B8"}
        size={20}
      />
      <div className={clsx("sidebar__links-label", isActive && "!text-white")}>{t(item.handle?.i18Key ?? "")} </div>
      {item.handle?.counter}
      {withChild && !item.handle?.ignoreChildren && (
        <Arrow className="sidebar__links-arrow -ml-1 min-h-9 min-w-8" color={isActive ? "#fff" : "#94A3B8"} size={24} />
      )}
    </Component>
  )
}
